import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../CSS/ListOfPlacements.css';

interface Placement {
  placementId: number;
  placementTitle: string;
  placementSource: string;
  placementSourceIcon: string;
  placementPicture: string;
  createdDate: string;
  lastUpdated: string;
  placementShortDesc: string;
  placementLongDesc: string;
}

const ListOfPlacements: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isFromAdmin = searchParams.get('fromAdmin') === 'true';
  const [searchQuery, setSearchQuery] = useState('');
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchPlacements = async () => {
      try {
        setIsLoading(true);
        // Production API endpoint
        // const response = await fetch('/api/placements');
        const response = await fetch('/api/placements');
        const data = await response.json();

        if (data.success && data.data) {
          // Sort by createdDate descending (most recent first) as a fallback
          const sortedPlacements = [...data.data].sort((a, b) => {
            const dateA = new Date(a.createdDate).getTime();
            const dateB = new Date(b.createdDate).getTime();
            return dateB - dateA; // Most recent first
          });
          console.log('Placements fetched:', sortedPlacements.length);
          // Log image paths for debugging
          sortedPlacements.forEach((placement: Placement) => {
            console.log('Placement data:', {
              placementId: placement.placementId,
              placementTitle: placement.placementTitle,
              placementPicture: placement.placementPicture,
              placementSourceIcon: placement.placementSourceIcon,
              constructedUrl: placement.placementPicture ? getImageUrl(placement.placementPicture) : 'N/A'
            });
            if (!placement.placementPicture || placement.placementPicture.trim() === '') {
              console.warn('Placement missing placementPicture:', {
                placementId: placement.placementId,
                placementTitle: placement.placementTitle,
                placementPicture: placement.placementPicture
              });
            }
          });
          setPlacements(sortedPlacements);
        } else {
          console.error('Failed to fetch placements:', data);
          setPlacements([]);
        }
      } catch (error) {
        console.error('Error fetching placements:', error);
        setPlacements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlacements();
  }, []);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to convert database file path to API endpoint URL
  const getImageUrl = (path: string | null | undefined): string => {
    if (!path || path.trim() === '') {
      console.warn('Placement image path is empty or null');
      return '/placeholder-image.jpg';
    }
    // If path already starts with http, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // Convert database path to API endpoint
    // Database path: Files/Placement Images/filename.png
    // API endpoint: /api/placements/file/Files/Placement Images/filename.png
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    console.log('Placement image URL:', { originalPath: path, cleanPath });
    // Production API endpoint
    // return `/api/placements/file/${cleanPath}`;
    return `/api/placements/file/${cleanPath}`;
  };

  // Helper function to strip HTML tags from text for searching
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Filter placements based on search query - search through all available placement fields
  const filteredPlacements = searchQuery.trim() === ''
    ? placements
    : placements.filter(placement => {
        const searchLower = searchQuery.toLowerCase();
        const longDescText = stripHtmlTags(placement.placementLongDesc || '').toLowerCase();
        
        return (
          placement.placementTitle.toLowerCase().includes(searchLower) ||
          placement.placementShortDesc.toLowerCase().includes(searchLower) ||
          placement.placementSource.toLowerCase().includes(searchLower) ||
          longDescText.includes(searchLower)
        );
      });

  // Calculate pagination
  const totalItems = filteredPlacements.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPlacements = filteredPlacements.slice(startIndex, endIndex);

  // Generate page numbers to display (show up to 10 page numbers)
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 10;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if 10 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, current page area, and last page
      if (currentPage <= 5) {
        // Near the beginning
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 4) {
        // Near the end
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        for (let i = currentPage - 4; i <= currentPage + 5; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="list-of-placements-page" style={isFromAdmin ? { marginTop: '10vh' } : {}}>
      {/* Dark Blue Banner */}
      <div className="list-placements-banner">
        <div className="list-placements-breadcrumb">
          <Link to="/">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/news-and-insights">News & Insights</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Recent Placements</span>
        </div>
        <h1 className="list-placements-title">Recent Placements</h1>
      </div>

      {/* White Content Area */}
      <div className="list-placements-content">
        <div className="list-placements-search-container">
          <div className="list-placements-search-bar">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="list-placements-search-input"
            />
            <button className="list-placements-search-icon-button" aria-label="Search">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Placements List */}
        {isLoading ? (
          <div className="list-placements-loading">Loading placements...</div>
        ) : placements.length === 0 ? (
          <div className="list-placements-empty">
            There are no recent placement news. Please wait some time and check back again at a later date.
          </div>
        ) : filteredPlacements.length === 0 ? (
          <div className="list-placements-empty">
            No placements found matching your search.
          </div>
        ) : (
          <>
            <div className="list-placements-grid">
              {currentPlacements.map((placement) => (
                <div key={placement.placementId} className="placement-card">
                  <div className="placement-card-image">
                    <img
                      src={getImageUrl(placement.placementPicture)}
                      alt={placement.placementTitle}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </div>
                  <div className="placement-card-content">
                    <div className="placement-card-date">
                      {formatDate(placement.createdDate)}
                    </div>
                    <h2 className="placement-card-title">
                      <Link 
                        to={`/news-and-insights/recent-placements/${placement.placementId}`}
                        className="placement-title-link"
                      >
                      {placement.placementTitle}
                      </Link>
                    </h2>
                    <p className="placement-card-description">
                      {placement.placementShortDesc}
                    </p>
                    <div className="placement-card-source">
                      {placement.placementSourceIcon && (
                        <img
                          src={getImageUrl(placement.placementSourceIcon)}
                          alt={placement.placementSource}
                          className="placement-source-icon"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="placement-source-text">
                        {placement.placementSource}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="list-placements-pagination">
                <div className="pagination-info">
                  Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} entries
                </div>
                <div className="pagination-controls">
                  {getPageNumbers().map((page, index) => (
                    <button
                      key={index}
                      className={`pagination-button ${currentPage === page ? 'active' : ''}`}
                      onClick={() => typeof page === 'number' && handlePageChange(page)}
                      disabled={typeof page !== 'number'}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ListOfPlacements;

